<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { usePlanningStore } from '@/stores/planning.store'
import { planningApi } from '@/apis/planning.api'
import { useChips } from '@/composables/chips'
import ChipTemplate from '@/components/shared/ChipTemplate.vue'

const { t } = useI18n()
const planningStore = usePlanningStore()
const { getServiceStructureTypeChip } = useChips()

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  service: { type: Object, default: null },
})

const emit = defineEmits(['update:modelValue', 'confirm'])

const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})

const quantity = ref(1)
const loading = ref(false)
const treeNodes = ref([])
const allNodes = ref([])
const tickedKeys = ref([])
const expandedKeys = ref([])

const toArray = v => Array.isArray(v) ? v : (v?.data ?? v?.results ?? [])

// Build the (possibly DAG-unfolded) component tree from the flat backend list.
const buildTree = (flat) => {
  // Distinct direct parent→child edges (the flat list duplicates them per path)
  const seen = new Set()
  const childrenByParent = new Map()
  for (const node of flat) {
    if (node.parent_service_id == null) continue
    const key = `${node.parent_service_id}-${node.service_id}`
    if (seen.has(key)) continue
    seen.add(key)
    if (!childrenByParent.has(node.parent_service_id)) childrenByParent.set(node.parent_service_id, [])
    childrenByParent.get(node.parent_service_id).push(node)
  }

  const nodes = []
  let counter = 0
  const build = (parentServiceId, path) => (childrenByParent.get(parentServiceId) || [])
    .filter(node => !path.has(node.service_id))
    .map(node => {
      const svc = planningStore.getServiceById(node.service_id)
      const treeNode = {
        key: `n${counter++}`,
        service_id: node.service_id,
        is_leaf: node.is_leaf,
        label: svc ? planningStore.getServiceName(svc) : `#${node.service_id}`,
        quantity: node.quantity,
        structure_type: svc?.structure_type ?? node.structure_type,
        children: node.is_leaf ? undefined : build(node.service_id, new Set([...path, node.service_id])),
      }
      nodes.push(treeNode)
      return treeNode
    })

  const roots = props.service ? build(props.service.id, new Set([props.service.id])) : []
  allNodes.value = nodes
  return roots
}

const leafKeys = computed(() => allNodes.value.filter(n => n.is_leaf).map(n => n.key))

const selectedLeafServiceIds = computed(() => {
  const keyToService = new Map(allNodes.value.map(n => [n.key, n.service_id]))
  return [...new Set(tickedKeys.value.map(k => keyToService.get(k)).filter(Boolean))]
})

const isValid = computed(() => quantity.value >= 1 && selectedLeafServiceIds.value.length > 0)

const getNodeChips = (node) => getServiceStructureTypeChip(node.structure_type)

const loadTree = async () => {
  if (!props.service?.id) { treeNodes.value = []; return }
  loading.value = true
  try {
    const res = await planningApi.getServiceComponentTreeAsync(props.service.id)
    treeNodes.value = buildTree(toArray(res))
    // Default: all leaves selected, whole tree expanded
    tickedKeys.value = [...leafKeys.value]
    expandedKeys.value = allNodes.value.filter(n => !n.is_leaf).map(n => n.key)
  } catch (error) {
    console.error('Failed to load component tree', error)
    treeNodes.value = []
    tickedKeys.value = []
  } finally {
    loading.value = false
  }
}

const onConfirm = () => {
  emit('confirm', {
    quantity: quantity.value,
    selectedComponentIds: selectedLeafServiceIds.value,
  })
  visible.value = false
}

const onCancel = () => {
  visible.value = false
}

const onShow = () => {
  quantity.value = 1
  tickedKeys.value = []
  expandedKeys.value = []
  loadTree()
}
</script>

<template>
  <q-dialog
    v-model="visible"
    persistent
    class="dialog-xs"
    @show="onShow"
  >
    <q-card>
      <q-card-section>
        <div class="text-h6">{{ service ? planningStore.getServiceName(service) : '' }}</div>
        <div class="text-caption text-grey-7">
          {{ t('common.selectComponentsToAllocate') }}
          <q-icon
            name="mdi-information-outline"
            color="info"
            size="xs"
            class="cursor-pointer q-ml-xs"
          >
            <q-tooltip max-width="300px" class="text-body2">
              {{ t('common.groupComponentsHint') }}
            </q-tooltip>
          </q-icon>
        </div>
      </q-card-section>

      <q-card-section class="q-pt-none">
        <q-input
          v-model.number="quantity"
          type="number"
          :min="1"
          :label="t('common.quantity')"
          :rules="[val => val >= 1 || t('common.required')]"
          outlined
          dense
          class="q-mb-md"
        />

        <q-separator class="q-mb-sm" />

        <div v-if="loading" class="row justify-center q-pa-md">
          <q-spinner color="primary" size="2em" />
        </div>

        <q-tree
          v-else
          v-model:ticked="tickedKeys"
          v-model:expanded="expandedKeys"
          :nodes="treeNodes"
          node-key="key"
          label-key="label"
          tick-strategy="leaf"
        >
          <template #default-header="prop">
            <div class="row items-center no-wrap full-width">
              <div class="col ellipsis">{{ prop.node.label }}</div>
              <chip-template
                v-if="getNodeChips(prop.node).length"
                :values="getNodeChips(prop.node)"
                class="q-mx-xs"
                dense
              />
              <q-badge color="grey-6" :label="`x${Math.round(prop.node.quantity)}`" />
            </div>
          </template>
        </q-tree>
      </q-card-section>

      <q-card-actions align="right">
        <q-btn flat :label="t('common.cancel')" @click="onCancel" />
        <q-btn
          color="primary"
          :label="t('common.confirm')"
          :disable="!isValid"
          @click="onConfirm"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
